import { useAutoAnimate } from '@formkit/auto-animate/react';
import { PlusIcon } from '@heroicons/react/20/solid';
import { useAppSelector } from 'app/hooks';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger
} from 'components/layout/popover';
import {
  aboveGradeGlassTypes,
  belowGradeGlassTypes,
  doorTypes
} from 'data/constants';
import {
  bayWindow,
  frenchDoor,
  largeWindow,
  mediumWindow,
  slidingGlassDoor,
  smallWindow,
  solidDoor
} from 'data/sizing-calculator-defaults';
import {
  AboveGradeWallType,
  BelowGradeWallType,
  DoorItemType,
  GlassItemType,
  SizingItemsType
} from 'data/types';
import { nanoid } from 'nanoid';
import { normalizeSolidDoorAttributes } from 'utils/sizing';
import SolidDoorComponent from '../fenestration/solid-door';
import VerticalGlassComponent from '../fenestration/vertical-glass';

type WallFenestrationsProps =
  | {
      wallType: 'Above Grade';
      wall: AboveGradeWallType;
      updateFn: (wallId: string, updates: Partial<AboveGradeWallType>) => void;
      updateDefaultSizingFn: (updates: Partial<SizingItemsType>) => void;
      focusFenestrationId?: string;
    }
  | {
      wallType: 'Below Grade';
      wall: BelowGradeWallType;
      updateFn: (wallId: string, updates: Partial<BelowGradeWallType>) => void;
      updateDefaultSizingFn: (updates: Partial<SizingItemsType>) => void;
      focusFenestrationId?: string;
    };

export default function WallFenestrations({
  wall,
  updateFn,
  updateDefaultSizingFn,
  focusFenestrationId
}: WallFenestrationsProps) {
  const [animate] = useAutoAnimate({ duration: 100 });
  const { defaultSizingItems, validSizingAttributes } = useAppSelector(
    (state) => state.data
  );

  const addVerticalGlass = (
    glassType:
      | (typeof aboveGradeGlassTypes)[number]
      | (typeof belowGradeGlassTypes)[number]
  ) => {
    const glassOptions = {
      'Small Window': smallWindow,
      'Medium Window': mediumWindow,
      'Large Window': largeWindow,
      'Sliding Glass Door': slidingGlassDoor,
      'Bay Window': bayWindow,
      'French Door': frenchDoor
    };

    const newGlass: GlassItemType = {
      ...defaultSizingItems.verticalGlass,
      ...glassOptions[glassType],
      uniqueId: nanoid(5),
      valid: true
    } as GlassItemType;

    updateFn(wall.uniqueId, {
      verticalGlass: [...(wall.verticalGlass || []), newGlass]
    });
  };

  const duplicateVerticalGlass = (uniqueId: string) => {
    const glassItem = (wall.verticalGlass || []).find(
      (g) => g.uniqueId === uniqueId
    );

    if (glassItem) {
      updateFn(wall.uniqueId, {
        verticalGlass: [
          ...(wall.verticalGlass || []),
          {
            ...glassItem,
            uniqueId: nanoid(5)
          }
        ]
      });
    }
  };

  const deleteVerticalGlass = (uniqueId: string) => {
    updateFn(wall.uniqueId, {
      verticalGlass: (wall.verticalGlass || []).filter(
        (glass) => glass.uniqueId !== uniqueId
      )
    });
  };

  const addSolidDoor = (doorType: (typeof doorTypes)[number]) => {
    const material = doorType === 'Metal Door' ? 'Metal' : 'Wood';

    const doorDraft: DoorItemType = {
      ...defaultSizingItems.solidDoor,
      ...solidDoor,
      material,
      uniqueId: nanoid(5),
      valid: true
    } as DoorItemType;

    const newDoor = normalizeSolidDoorAttributes(
      doorDraft,
      validSizingAttributes.solidDoors
    ) as DoorItemType;

    updateFn(wall.uniqueId, {
      solidDoors: [...(wall.solidDoors || []), newDoor]
    });
  };

  const duplicateSolidDoor = (uniqueId: string) => {
    const doorItem = (wall.solidDoors || []).find(
      (d) => d.uniqueId === uniqueId
    );

    if (doorItem) {
      updateFn(wall.uniqueId, {
        solidDoors: [
          ...(wall.solidDoors || []),
          { ...doorItem, uniqueId: nanoid(5) }
        ]
      });
    }
  };

  const deleteSolidDoor = (uniqueId: string) => {
    updateFn(wall.uniqueId, {
      solidDoors: (wall.solidDoors || []).filter(
        (door) => door.uniqueId !== uniqueId
      )
    });
  };

  const glassButtonDescription = 'Window/Glass Door';

  const validGlassOptions = aboveGradeGlassTypes;

  return (
    <div className='flex flex-col gap-3 px-3 pb-3' ref={animate}>
      {(wall.verticalGlass || []).map((glass) => (
        <VerticalGlassComponent
          key={glass.uniqueId}
          glass={glass}
          updateFn={updateFn}
          duplicateFn={duplicateVerticalGlass}
          deleteFn={deleteVerticalGlass}
          updateDefaultSizingFn={updateDefaultSizingFn}
          defaultExpandAdvanced={glass.uniqueId === focusFenestrationId}
        />
      ))}
      {(wall.solidDoors || []).map((door) => (
        <SolidDoorComponent
          key={door.uniqueId}
          door={door}
          updateFn={updateFn}
          duplicateFn={duplicateSolidDoor}
          deleteFn={deleteSolidDoor}
          updateDefaultSizingFn={updateDefaultSizingFn}
          defaultExpandAdvanced={door.uniqueId === focusFenestrationId}
        />
      ))}
      <div className='flex items-center gap-3'>
        <Popover placement='bottom-start'>
          <div>
            <PopoverTrigger className='inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md bg-blue-100 px-3 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'>
              <PlusIcon className='h-4 w-4' />
              {glassButtonDescription}
            </PopoverTrigger>
          </div>
          <PopoverContent className='z-50 flex w-56 flex-col rounded-lg bg-white p-2 shadow-lg ring-1 ring-black/5 focus:outline-none'>
            {validGlassOptions.map((glassType) => (
              <PopoverClose
                key={glassType}
                onClick={() => addVerticalGlass(glassType)}
                className='flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-semibold text-gray-900 hover:bg-gray-100'
              >
                <span>{glassType}</span>
              </PopoverClose>
            ))}
          </PopoverContent>
        </Popover>
        <Popover placement='bottom-start'>
          <div>
            <PopoverTrigger className='inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md bg-blue-100 px-3 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'>
              <PlusIcon className='h-4 w-4' />
              Solid Door
            </PopoverTrigger>
          </div>
          <PopoverContent className='z-50 flex w-56 flex-col rounded-lg bg-white p-2 shadow-lg ring-1 ring-black/5 focus:outline-none'>
            {doorTypes.map((doorType) => (
              <PopoverClose
                key={doorType}
                onClick={() => addSolidDoor(doorType)}
                className='flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-semibold text-gray-900 hover:bg-gray-100'
              >
                <span>{doorType}</span>
              </PopoverClose>
            ))}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
